import { useAuth0 } from "@auth0/auth0-react";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Fab,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  useTheme,
  Chip,
  Divider,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import CreateLeague from "../components/createleague/CreateLeague";
import JoinLeague from "../components/joinleague/JoinLeague";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../hooks/isMobile/isMobile";
import { useLeague } from "../hooks/useLeague/useLeague";
import { LeagueMini } from "../types/league";

const Leagues = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { getLeagues } = useLeague();
  const [error, setError] = useState<string | null>(null);
  const [createLeagueDialogOpen, setCreateLeagueDialogOpen] =
    React.useState<boolean>(false);
  const [joinLeagueDialogOpen, setJoinLeagueDialogOpen] =
    React.useState<boolean>(false);
  const [leagues, setLeagues] = useState<LeagueMini[] | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const fetchData = useCallback(async () => {
    if (!isLoading && isAuthenticated && user) {
      setLoading(true);
      try {
        const response = await getLeagues();
        if (response instanceof Error) {
          throw new Error(response.message);
        }
        setLeagues(response.leagues);
      } catch (err: any) {
        console.error("Failed to fetch leagues:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  }, [isAuthenticated, isLoading, user, getLeagues]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!isLoading && !isAuthenticated) {
    return <div>Sign in to view your leagues</div>;
  }

  if (loading) {
    return <></>;
  }

  const open = Boolean(anchorEl);

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const handleCreateLeagueOpen = () => {
    setCreateLeagueDialogOpen(true);
  };

  const handleCreateLeagueClose = () => {
    setCreateLeagueDialogOpen(false);
    fetchData();
  };

  const handleJoinLeagueOpen = () => {
    setJoinLeagueDialogOpen(true);
  };

  const handleJoinLeagueClose = () => {
    setJoinLeagueDialogOpen(false);
  };

  const renderDesktopTable = () => (
    <TableContainer
      component={Paper}
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="league table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>League Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Created On</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Members</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leagues?.map((value, index) => (
            <TableRow
              key={value.id}
              onClick={() => navigate(`/leagues/${value.id}`)}
              hover
              sx={{
                cursor: "pointer",
                "&:last-child td": { border: 0 },
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 4px 8px rgba(0,0,0,0.3)"
                      : "0 4px 8px rgba(0,0,0,0.1)",
                },
                "&:nth-of-type(even)": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : theme.palette.grey[50],
                },
              }}
            >
              <TableCell
                sx={{ fontWeight: 500, color: theme.palette.primary.main }}
              >
                {value.name}
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarTodayIcon
                    sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                  />
                  {format(new Date(value.created * 1000), "MMM dd, yyyy")}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <GroupIcon
                    sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                  />
                  <Chip
                    label={value.memberCount}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ minWidth: 40 }}
                  />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileCards = () => (
    <Grid container spacing={2}>
      {leagues?.map((value) => (
        <Grid item xs={12} key={value.id}>
          <Card
            elevation={2}
            onClick={() => navigate(`/leagues/${value.id}`)}
            sx={{
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
              },
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  mb: 1,
                }}
              >
                {value.name}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarTodayIcon
                    sx={{ fontSize: 18, color: theme.palette.text.secondary }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(value.created * 1000), "MMM dd, yyyy")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <GroupIcon
                    sx={{ fontSize: 18, color: theme.palette.text.secondary }}
                  />
                  <Chip
                    label={`${value.memberCount} members`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <HelmetProvider>
      <Helmet defer={false}>
        <title>Pickems</title>
      </Helmet>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: theme.palette.primary.main,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          My Leagues
        </Typography>

        {leagues && leagues.length > 0 ? (
          isMobile ? (
            renderMobileCards()
          ) : (
            renderDesktopTable()
          )
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No leagues found!
            </Typography>
            <Typography variant="body1">
              Create or join a league to get started
            </Typography>
          </Box>
        )}
      </Box>

      <Fab
        variant="extended"
        color="primary"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          "&:hover": {
            boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
          },
        }}
        onClick={onClick}
      >
        <AddIcon
          sx={{
            mr: 1,
            transition: "transform 0.3s ease",
            transform: open ? "rotate(45deg)" : "rotate(0deg)",
          }}
        />
        Create or Join
      </Fab>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={onClose}
        onClick={onClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              mt: "16px",
              pb: "16px",
              width: anchorEl ? anchorEl.offsetWidth : "auto",
            },
          },
        }}
        sx={{
          zIndex: 1,
        }}
        transformOrigin={{ horizontal: "center", vertical: "bottom" }}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <MenuItem onClick={handleCreateLeagueOpen}>Create a League</MenuItem>
        <MenuItem onClick={handleJoinLeagueOpen}>Join with Code</MenuItem>
      </Menu>
      <CreateLeague
        open={createLeagueDialogOpen}
        onClose={handleCreateLeagueClose}
      />
      <JoinLeague open={joinLeagueDialogOpen} onClose={handleJoinLeagueClose} />
    </HelmetProvider>
  );
};

export default Leagues;
